'use client';

import { ChakraProvider } from '@chakra-ui/react';
import { ColorModeProvider, type ColorModeProviderProps } from './color-mode';
import { BrandKey, makeTheme, resolveBrandKey } from '@themes/aegix';
import { getBrandKeyFromAuthCookie } from '../../utils/brandKeyFromJwt';

/**
 * Brand keys sms-web is allowed to render.
 *
 * Per ADR 115 §4 — sms-web supports `aegix` and `safe-plus`. The SAFE+
 * chrome renders at the `safe-ms.{env}.aegix.global` hostname (pre-auth,
 * via the hostname layer of the resolver) and for any audience whose JWT
 * carries `brand_key=safe-plus` (post-auth, via the JWT claim layer).
 *
 * DIRS is intentionally excluded — sms-web has no DIRS surface and
 * legacy DIRS is end-of-life. A DIRS JWT claim arriving at sms-web
 * silently falls back to Aegix via the supportedBrands filter.
 *
 * The live SAFE+ MS experience also depends on the CloudFront
 * distribution + DNS for `safe-ms.{env}.aegix.global`; the unit tests
 * exercise this code path without that infra.
 */
const SMS_SUPPORTED_BRANDS = [BrandKey.Aegix, BrandKey.SafePlus] as const;

export function Provider(props: ColorModeProviderProps) {
  // ADR 115 §3: layered resolver. JWT brand_key > hostname > AEGIX fallback.
  // SAFE+ tenants render SAFE+ chrome here.
  const brandKey = resolveBrandKey({
    jwtClaim: getBrandKeyFromAuthCookie(),
    hostname:
      typeof window !== 'undefined' ? window.location.hostname : undefined,
    supportedBrands: SMS_SUPPORTED_BRANDS,
  });

  // ADR 115 Phase 5: makeTheme takes the BrandKey enum directly.
  const theme = makeTheme(brandKey);

  return (
    <ChakraProvider value={theme}>
      <ColorModeProvider {...props} />
    </ChakraProvider>
  );
}
